import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import Spinner from "../../components/Shared/Spinner";
import { GetIFRSById } from "../../api/Quiz/GetIFRSById";
import { GetAdminIFRSQuestions } from "../../api/Quiz/GetAdminIFRSQuestions";
import IFRSQuestionsTable from "../../components/Dashboard/IFRSExams/IFRSQuestionsTable";
import Button from "../../components/Shared/Button";
import AddIcon from "../../components/Icons/AddIcon";

const IFRSExamQuestions = () => {
  const { exam_id, courseId } = useParams<{
    exam_id: string;
    courseId: string;
  }>();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 10;

  // Fetch exam details for title
  const { data: IFRSResponse, isLoading: isIFRSLoading } = useQuery({
    queryFn: () => GetIFRSById(exam_id || ""),
    queryKey: ["ifrs", exam_id],
    enabled: !!exam_id,
    refetchOnWindowFocus: false,
  });

  // Fetch questions with pagination using admin API
  const { data: QuestionsResponse, isLoading: isQuestionsLoading } = useQuery({
    queryFn: () =>
      GetAdminIFRSQuestions(exam_id || "", { page, limit, sort: "asc" }),
    queryKey: ["ifrs-questions", exam_id, page, limit],
    enabled: !!exam_id,
    staleTime: 0,
    refetchInterval: 0,
    refetchOnWindowFocus: false,
  });

  const handleDelete = () => {
    // Invalidate the query to refresh the data after deletion
    queryClient.invalidateQueries({ queryKey: ["ifrs-questions", exam_id] });
  };

  const questions = QuestionsResponse?.data?.data || [];
  const meta = QuestionsResponse?.data?.meta;
  const examTitle = IFRSResponse?.data?.title || "IFRS Exam";

  if (isIFRSLoading || isQuestionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="py-7">
      <div className="flex items-center gap-4 mb-7">
        <Link
          to={`/dashboard/courses/${courseId}/ifrs-exam`}
          className="text-primary hover:text-primary/80 font-semibold"
        >
          ← Back to IFRS Exams
        </Link>
        <h1 className="text-burntBlack font-bold text-[32px]">
          {examTitle} - Questions
        </h1>
      </div>
      <div className="p-8 rounded-xl bg-white border-black/15 shadow">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-bold text-2xl">Questions List</h1>
          <Link to={`create`}>
            <Button
              className="!h-12 w-48 !text-sm font-bold rounded-md !bg-[#4379EE] hover:!bg-primary"
              variant="primary"
            >
              <AddIcon className="mr-1.5" />
              Create Question
            </Button>
          </Link>
        </div>

        <IFRSQuestionsTable
          data={questions}
          examId={exam_id || ""}
          onDelete={handleDelete}
        />

        {meta && (
          <div className="flex justify-between items-center mt-6">
            <div className="text-sm text-gray-600">
              Showing {questions.length} of {meta.total} questions
            </div>
            <div className="flex gap-2">
              <Button
                variant="gray"
                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                disabled={!meta.hasPreviousPage || isQuestionsLoading}
                className="!h-10 !text-sm"
              >
                Previous
              </Button>
              <div className="flex items-center px-4 text-sm font-semibold">
                Page {meta.page} of {meta.lastPage}
              </div>
              <Button
                variant="gray"
                onClick={() => setPage((prev) => prev + 1)}
                disabled={!meta.hasNextPage || isQuestionsLoading}
                className="!h-10 !text-sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IFRSExamQuestions;
